import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Typography,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import React, { useCallback, useState } from "react";
import formItemProps from "../../helper/form/formItemProps";
import {
  CouponVerificationOutput,
  Product,
  useVerifyCouponsMutation,
} from "../../graphql/schema";
import getPriceForCustomer from "../../helper/common/getPriceForCustomer";
import useBooking from "../bookingProvider/useBooking";
import { ProductWithVariation } from "../bookingProvider/BookingContext";
import theme from "../../theme/theme";
import getProductByBookedProduct from "../../helper/common/getProductByBookedProduct";
import CouponLabel from "../confirmation/CouponLabel";

interface Props {
  open: boolean;
  onClose: () => void;
  products: any[];
}

const CouponDrawer = ({ open, onClose, products }: Props) => {
  const { stateValues, setStates, getStates } = useBooking();
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [form] = Form.useForm();
  const [verify] = useVerifyCouponsMutation();

  const getCouponCodes = () => {
    const { couponCodes } = getStates(["couponCodes"]);
    return couponCodes?.length ? couponCodes : [];
  };

  const removeLastCouponCode = () => {
    const codes = getCouponCodes();
    if (codes.length) {
      codes.splice(codes.length - 1, 1);
    }
    setStates({
      ...stateValues,
      couponCodes: codes,
    });
  };
  const getBookedProducts = () => {
    const { bookedProducts } = getStates(["bookedProducts"]);
    return bookedProducts?.length ? bookedProducts : [];
  };

  const resetPrices = useCallback(() => {
    const bookedProducts = getBookedProducts();
    setStates({
      ...stateValues,
      couponCodes: [],
      bookedProducts: bookedProducts.map((product) => {
        let price = "0";
        if (product.variation) {
          const variation = products
            .find((p: Product) => p.id === product.id)
            .variations.find(
              (variation: any) => variation.id === product.variation,
            );
          if (variation?.prices)
            price = getPriceForCustomer(
              variation.prices,
              stateValues.customer,
            ).toString();
        } else {
          price = getPriceForCustomer(
            products.find((p: Product) => p.id === product.id).prices,
            stateValues.customer,
          ).toString();
        }
        return {
          ...product,
          price: price.toString(),
        };
      }),
    });
  }, [getStates, setStates, stateValues]);

  const setNewPriceForBookedProducts = (
    bookedProducts: CouponVerificationOutput[] | undefined,
    codes: string[],
  ) => {
    if (!bookedProducts) return;
    const verifiedBookedProducts: ProductWithVariation[] = bookedProducts.map(
      (productOutput) => {
        const product: ProductWithVariation = {
          ...productOutput,
          id: productOutput.productId,
          name: "",
          category: "",
          variation: productOutput.variationId
            ? productOutput.variationId
            : undefined,
          price: productOutput.price ? productOutput.price : null,
        };

        const baseProduct = getProductByBookedProduct(product, products);
        const variation = baseProduct?.variations?.find(
          (variation: any) => variation.id === product.variation,
        );
        const taxRate = baseProduct?.taxRate || 0;
        const netPrice = Number(
          Number(product.price) / (1 + taxRate / 100),
        ).toFixed(2);
        return {
          ...product,
          id: product.id,
          variation: product.variation,
          price: product.price,
          netPrice,
          baseName: baseProduct?.name || "",
          name: variation?.name || baseProduct?.name || "",
          category: baseProduct?.productCategory?.name || "",
        };
      },
    );

    setStates({
      ...stateValues,
      bookedProducts: verifiedBookedProducts,
      couponCodes:
        JSON.stringify(verifiedBookedProducts) ===
        JSON.stringify(stateValues.bookedProducts)
          ? codes.filter((code) => code !== codes.pop())
          : codes,
    });
    if (codes.length < getCouponCodes().length) {
      if (codes.length === 0) {
        resetPrices();
      }
      message.success("Gutschein entfernt");
    } else if (
      JSON.stringify(verifiedBookedProducts) ===
      JSON.stringify(stateValues.bookedProducts)
    ) {
      message.error("Gutschein nicht gültig");
    } else {
      message.success("Gutschein angewendet");
    }
  };

  const verifyCoupon = useCallback(
    async (codes?: string[]): Promise<boolean> => {
      const bookedProducts = getBookedProducts();
      if (!bookedProducts) return false;
      try {
        const response = await verify({
          variables: {
            dto: {
              products: bookedProducts.map((product) => ({
                productId: product.id,
                variationId: product.variation ? product.variation : undefined,
                price: product.price ? product.price.toString() : null,
              })),
              couponCodes: codes || getCouponCodes(),
              customer: {
                id: stateValues.customer.id || 0,
              },
            },
          },
        });
        setNewPriceForBookedProducts(
          response.data?.verifyCoupons || [],
          codes || getCouponCodes(),
        );
      } catch (error) {
        removeLastCouponCode();
        message.error("Gutschein nicht gültig");
      }
      return true;
    },
    [getStates, setStates, stateValues],
  );

  const addCouponCode = async (code: string) => {
    setValidatingCoupon(true);
    const codes = getCouponCodes();
    if (!codes.includes(code)) {
      codes.push(code);
      await verifyCoupon(codes);

      setValidatingCoupon(false);
    } else {
      message.error("Gutschein wurde bereits hinzugefügt");
      setValidatingCoupon(false);
    }
    form.setFieldValue("coupon", "");
  };

  const removeCouponCode = async (code: string) => {
    const codes = getCouponCodes().filter((c) => c !== code);
    await verifyCoupon(codes);
  };
  return (
    <Drawer
      contentWrapperStyle={{
        height: 300,
      }}
      style={{
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
      }}
      open={open}
      closeIcon={null}
      placement="bottom"
      onClose={() => onClose()}
      title=""
      footer={
        <Button
          style={{
            backgroundColor: theme.colors.button,
            width: "100%",
            height: 49,
            bottom: "0px",
            borderRadius: "15px",
          }}
          onClick={() => addCouponCode(form.getFieldValue("couponCode"))}
        >
          <Typography.Title
            level={5}
            style={{
              color: "white",
              marginBottom: "0px",
            }}
          >
            Hinzufügen
          </Typography.Title>
        </Button>
      }
    >
      <Row justify="end">
        <Col>
          <CloseOutlined
            style={{
              fontSize: "20px",
              color: theme.colors.disabled,
            }}
            onClick={onClose}
          />
        </Col>
      </Row>
      <Form form={form}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Typography.Title level={5}>Gutschein einlösen</Typography.Title>
          </Col>
          <Col span={24}>
            {getCouponCodes().map((code) => (
              <CouponLabel code={code} remove={removeCouponCode} />
            ))}
          </Col>
          <Col span={24}>
            <Form.Item
              hasFeedback={validatingCoupon}
              validateStatus={validatingCoupon ? "validating" : undefined}
              {...formItemProps("couponCode", "")}
            >
              <Input
                size="large"
                style={{
                  borderRadius: 15,
                  height: 47,
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default CouponDrawer;
